import { Injectable } from '@angular/core';

export interface PersistenceSettings {
  persistVolume: boolean;
}

export interface VideoSettings {
  fullscreenControlsPosition: string;
}

export interface ImageSettings {
  imageDisplayDuration: number;
  imageAutoAdvance: boolean;
  animatedGifBehavior: string;
  pinchZoomEnabled: boolean;
}

/**
 * Service responsible for managing search-related settings persistence.
 * Handles load/save operations for persistence, video, image, and queue settings.
 * Extracted from SearchComponent to reduce component complexity.
 */
@Injectable({
  providedIn: 'root',
})
export class SearchSettingsService {
  private readonly PERSISTENCE_SETTINGS_KEY = 'drawfiend-persistence-settings';
  private readonly VIDEO_SETTINGS_KEY = 'drawfiend-video-settings';
  private readonly IMAGE_SETTINGS_KEY = 'drawfiend-image-settings';
  private readonly QUEUE_PERSIST_KEY = 'drawfiend-queue-persist';

  // ─── Persistence Settings ───────────────────────────────────────────────

  /**
   * Load persistence settings from localStorage
   */
  loadPersistenceSettings(): PersistenceSettings {
    try {
      const saved = localStorage.getItem(this.PERSISTENCE_SETTINGS_KEY);
      if (saved) {
        const settings = JSON.parse(saved);
        return {
          persistVolume: settings.persistVolume !== undefined ? settings.persistVolume : true,
        };
      }
    } catch (error) {
      console.warn('Failed to load persistence settings:', error);
    }

    return {
      persistVolume: true,
    };
  }

  /**
   * Save persistence settings to localStorage
   */
  savePersistenceSettings(persistVolume: boolean): void {
    try {
      const settings = { persistVolume };
      localStorage.setItem(this.PERSISTENCE_SETTINGS_KEY, JSON.stringify(settings));
      console.log('Persistence settings saved:', settings);
    } catch (error) {
      console.warn('Failed to save persistence settings:', error);
    }
  }

  // ─── Video Settings ─────────────────────────────────────────────────────

  /**
   * Load video settings from localStorage
   */
  loadVideoSettings(): VideoSettings {
    try {
      const saved = localStorage.getItem(this.VIDEO_SETTINGS_KEY);
      if (saved) {
        const settings = JSON.parse(saved);
        return {
          fullscreenControlsPosition: settings.fullscreenControlsPosition || 'bottom-right',
        };
      }
    } catch (error) {
      console.warn('Failed to load video settings:', error);
    }

    return {
      fullscreenControlsPosition: 'bottom-right',
    };
  }

  /**
   * Save video settings to localStorage
   */
  saveVideoSettings(settings: VideoSettings): void {
    try {
      localStorage.setItem(this.VIDEO_SETTINGS_KEY, JSON.stringify(settings));
      console.log('Video settings saved:', settings);
    } catch (error) {
      console.warn('Failed to save video settings:', error);
    }
  }

  // ─── Image Settings ─────────────────────────────────────────────────────

  /**
   * Load image settings from localStorage
   */
  loadImageSettings(): ImageSettings {
    try {
      const saved = localStorage.getItem(this.IMAGE_SETTINGS_KEY);
      if (saved) {
        const settings = JSON.parse(saved);
        return {
          imageDisplayDuration: Math.max(1, Math.min(60, settings.imageDisplayDuration || 5)),
          imageAutoAdvance: settings.imageAutoAdvance !== false,
          animatedGifBehavior: settings.animatedGifBehavior || 'once',
          pinchZoomEnabled: settings.pinchZoomEnabled === true,
        };
      }
    } catch (error) {
      console.warn('Failed to load image settings:', error);
    }

    return {
      imageDisplayDuration: 5,
      imageAutoAdvance: true,
      animatedGifBehavior: 'once',
      pinchZoomEnabled: false,
    };
  }

  /**
   * Save image settings to localStorage
   */
  saveImageSettings(settings: ImageSettings): void {
    try {
      localStorage.setItem(this.IMAGE_SETTINGS_KEY, JSON.stringify(settings));
      console.log('Image settings saved:', settings);
    } catch (error) {
      console.warn('Failed to save image settings:', error);
    }
  }

  // ─── Queue Persistence ──────────────────────────────────────────────────

  /**
   * Save queue persistence setting to localStorage
   */
  saveQueuePersistenceSetting(enabled: boolean): void {
    localStorage.setItem(this.QUEUE_PERSIST_KEY, JSON.stringify(enabled));
  }

  /**
   * Load queue persistence setting from localStorage
   */
  loadQueuePersistenceSetting(): boolean {
    const val = localStorage.getItem(this.QUEUE_PERSIST_KEY);
    return val ? JSON.parse(val) : true;
  }
}
